/**
 * The decisions behind "connect before signing" on an EVM deal (app#197), kept free of wagmi so
 * they can be tested as plain functions.
 *
 * Registration and wallet linking gate every purchase already (the api requires an account with a
 * linked wallet on each buyer route). What neither of them can grant is the *browser's* permission:
 * the extension has to authorise this origin once per browser, and a buyer who signed in by email
 * on a fresh machine reaches "Pay now" without that step ever having happened. Left alone, wagmi
 * answers with its own words — "Connector not connected" — and the screen offers nothing to click.
 */

import {
  isUserRejection,
  NoWalletConnectedError,
  WalletConnectionCancelledError,
} from 'src/utils/walletErrors';

import { isWindowOnlyConnector } from './connect/connectWindow';

const short = (address: string): string => `${address.slice(0, 6)}…${address.slice(-4)}`;

/** Case-insensitive: the same key checksums differently in different wallets. */
export const sameAddress = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/**
 * Spoken to the buyer when the extension hands over a different account than the one linked to
 * theirs. Signing with it would not steal anything — the contract refuses a stranger — but it
 * would cost a network fee for a guaranteed revert, and leave the buyer guessing why.
 */
export const walletMismatchMessage = (connected: string, linked: string): string =>
  `This browser's wallet is ${short(connected)}, but your account is linked to ${short(linked)}. Switch to that account in your wallet and try again.`;

export interface ConnectorCandidate<T> {
  connector: T;
  id: string;
  /** Whether the connector can reach a provider in this browser right now. */
  hasProvider: boolean;
}

/**
 * The connectors to ask, in the order to ask them (app#203).
 *
 * wagmi lists the generic `injected` connector and, next to it, every wallet announced through
 * EIP-6963. An announced wallet's own connector is the precise choice; the generic one falls back
 * to `window.ethereum`, which with several extensions installed is whichever won the race for that
 * global — so once anything announced itself, the generic connector is only ever a duplicate of
 * one of them and is left out, or the buyer would be asked by the same wallet twice.
 *
 * With several announced wallets there is no wallet-select UI (Tim's call, 2026-08-14), so the
 * order *is* the choice: the connector that answered last time goes first, then the rest in the
 * order they announced. A caller that must reach one particular account walks the list until a
 * wallet hands it over. Without a provider anywhere the list is empty, and the caller says so in
 * the buyer's words rather than letting the library do it.
 */
export const rankConnectors = <T>(
  candidates: readonly ConnectorCandidate<T>[],
  preferredId?: string
): T[] => {
  const reachable = candidates.filter((candidate) => candidate.hasProvider);
  const announced = reachable.filter((candidate) => candidate.id !== 'injected');
  const pool = announced.length > 0 ? announced : reachable;
  const preferred = preferredId ? pool.filter((candidate) => candidate.id === preferredId) : [];
  const others = pool.filter((candidate) => !preferred.includes(candidate));

  return [...preferred, ...others].map((candidate) => candidate.connector);
};

/** The part of a wagmi connector these decisions need — structural, so no wagmi import here. */
export interface ConnectorLike {
  id: string;
  type?: string;
  getProvider: () => Promise<unknown>;
}

/**
 * {@link rankConnectors} over wagmi's live connector list: probes each for a provider (a
 * synchronous `window.ethereum` read behind a promise for the injected one, the announced object
 * for an EIP-6963 one) and orders them.
 *
 * The WalletConnect and Coinbase SDK connectors are never among them (app#315): each always has a
 * provider — one it builds itself — and asking either here would start a pairing with nobody shown
 * the code, or open Coinbase's page under a tap that meant a browser wallet. Each is asked only
 * from its row in the connection window, where the person sees what to scan or tap.
 */
export const rankLiveConnectors = async <T extends ConnectorLike>(
  connectors: readonly T[],
  preferredId?: string
): Promise<T[]> => {
  const browserWallets = connectors.filter((connector) => !isWindowOnlyConnector(connector));
  const candidates = await Promise.all(
    browserWallets.map(async (connector) => ({
      connector,
      id: connector.id,
      hasProvider: Boolean(await connector.getProvider().catch(() => null)),
    }))
  );

  return rankConnectors(candidates, preferredId);
};

/** The wallet a deal action on an EVM chain must be signed with. */
export interface LinkedEvmWallet {
  address: string;
}

export interface Connection {
  address: string;
  chainId: number | undefined;
  /** Which connector holds it — what the memory records, and what a retry skips. */
  connectorId: string | undefined;
}

/** What {@link acquireSigner} needs from wagmi, as functions — so the walk can be tested dry. */
export interface SignerPorts<T extends ConnectorLike> {
  /** wagmi's account as it is *now*, not as of the render that started the call. */
  live: () => Connection | null;
  /** The connectors to ask, in order — {@link rankLiveConnectors}, as a rule. */
  candidates: () => Promise<readonly T[]>;
  /** Ask a wallet for an account; rejects with the wallet's error, 4001 when the buyer refuses. */
  connect: (connector: T) => Promise<Connection>;
  /** Drop a connection that handed over the wrong account, so the next attempt asks again. */
  disconnect: () => Promise<void>;
  /** Record which connector handed over the account that was wanted. */
  remember: (connectorId: string, address: string) => void;
}

/**
 * The account that will sign, connected first if it is not yet.
 *
 * With `wanted` the account has to be that one, and with several wallets installed the walk goes
 * on until one of them hands it over (app#203): a connection to the wrong account is dropped and
 * the next wallet is asked, and the one that answers is remembered so it is asked first next time.
 * A buyer who refuses any of the prompts stops the walk — that is a cancelled *connection*, not a
 * cancelled signature (app#204), and the screen says so. Only when every wallet has been asked and
 * none holds the account is the mismatch reported, naming the last account seen and the one
 * linked.
 *
 * Without `wanted` — a challenge signature, which is what *establishes* which wallet is meant —
 * the first wallet to answer is the signer.
 */
export const acquireSigner = async <T extends ConnectorLike>(
  ports: SignerPorts<T>,
  wanted: LinkedEvmWallet | undefined
): Promise<Connection> => {
  const matches = (address: string) => !wanted || sameAddress(address, wanted.address);
  const asked = new Set<string>();
  let mismatched: string | undefined;

  const live = ports.live();

  if (live) {
    if (matches(live.address)) {
      if (live.connectorId) ports.remember(live.connectorId, wanted?.address ?? live.address);

      return live;
    }

    // Drop the wrong one; keeping it connected would repeat the mismatch forever.
    await ports.disconnect();
    if (live.connectorId) asked.add(live.connectorId);
    mismatched = live.address;
  }

  const candidates = (await ports.candidates()).filter((connector) => !asked.has(connector.id));

  if (candidates.length === 0 && mismatched === undefined) {
    // Nothing to ask and nothing connected: the screen answers with `Connect Wallet` and the
    // connection window (app#315), not with advice on what to install.
    throw new NoWalletConnectedError();
  }

  for (const connector of candidates) {
    let connection: Connection;

    try {
      connection = await ports.connect(connector);
    } catch (error) {
      if (isUserRejection(error)) {
        throw new WalletConnectionCancelledError();
      }

      throw error;
    }

    if (matches(connection.address)) {
      ports.remember(connector.id, wanted?.address ?? connection.address);

      return { ...connection, connectorId: connector.id };
    }

    mismatched = connection.address;
    await ports.disconnect();
  }

  // `wanted` is set: without it every connection matches and the loop has returned — unless there
  // was nothing to connect, which threw above.
  throw new Error(walletMismatchMessage(mismatched as string, (wanted as LinkedEvmWallet).address));
};
